import React from 'react';
import { Avatar } from '@/components/ui/avatar';
import { User } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ImageGallery from './ImageGallery';

interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  imageUrl?: string;
  generatedImages?: string[];
  imagePrompt?: string;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  role,
  content,
  timestamp,
  imageUrl,
  generatedImages,
  imagePrompt,
}) => {
  const isUser = role === 'user';
  const formattedTime = timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  
  // Log the content for debugging
  if (role === 'assistant' && content) {
    console.log('Rendering assistant message content:', content);
  }
  
  return (
    <div className={`flex py-4 px-4 ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!isUser && (
        <Avatar className="bg-transparent mr-3 mt-1 h-8 w-8">
          <img src="/obsidianavatar.png" alt="ObsidianAI Avatar" className="h-full w-full object-cover rounded-full" />
        </Avatar>
      )}
      
      <div className={`max-w-[75%]`}>
        <div className="flex items-center gap-2 mb-1">
          <span className="font-semibold text-sm">
            {isUser ? 'You' : 'ObsidianAI+'}
          </span>
          <span className="text-xs text-muted-foreground">
            {formattedTime}
          </span>
        </div>
        
        <div 
          className={`rounded-2xl px-4 py-3 shadow-lg ${
            isUser 
              ? 'bg-obsidian-600 text-white rounded-tr-none' 
              : 'bg-secondary/60 rounded-tl-none'
          }`}
          style={{ 
            boxShadow: isUser 
              ? '0 8px 16px rgba(0, 0, 0, 0.3)' 
              : '0 8px 16px rgba(0, 0, 0, 0.2)' 
          }}
        >
          {imageUrl && (
            <img 
              src={imageUrl} 
              alt="User uploaded" 
              className="max-h-60 rounded-md mb-2 object-contain" 
            />
          )}
          
          {isUser ? (
            // Plain text for user messages
            <div className="whitespace-pre-wrap text-white">{content}</div>
          ) : (
            <>
              {/* Markdown for assistant messages */}
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {content || ''}
                </ReactMarkdown>
              </div>
              
              {/* Display generated images if available */}
              {generatedImages && generatedImages.length > 0 && imagePrompt && (
                <ImageGallery images={generatedImages} prompt={imagePrompt} />
              )}
            </>
          )}
        </div>
      </div>
      
      {isUser && (
        <Avatar className="bg-obsidian-600 ml-3 mt-1 h-8 w-8">
          <img src="/user-avatar.png" alt="User Avatar" className="h-full w-full object-cover rounded-full" />
        </Avatar>
      )}
    </div>
  );
};

export default ChatMessage;
